import { Injectable } from '@angular/core';

type DateInput = string | number | Date;

@Injectable({
	providedIn: 'root',
})
export class DateService {
	constructor() {}

	public formatDate(date: DateInput): string {
		const baseDate = this.buildBaseDate(date);
		return baseDate.toLocaleDateString();
	}

	public formatDateTime(date: DateInput): string {
		const baseDate = this.buildBaseDate(date);
		return baseDate.toLocaleString();
	}

	public formatTime(date: DateInput): string {
		const baseDate = this.buildBaseDate(date);
		return baseDate.toLocaleTimeString();
	}

	public getCurrentDate(): Date {
		return new Date();
	}

	private buildBaseDate(date: DateInput) {
		if (date instanceof Date) {
			return date;
		}

		// Normalize numeric-like inputs that may be Unix timestamps
		const numericCandidate = Number(String(date));
		if (!Number.isNaN(numericCandidate)) {
			// If it's already in milliseconds
			if (isUnixTimestampWithMilliseconds(numericCandidate)) {
				return new Date(numericCandidate);
			}
			// If it's in seconds, convert to milliseconds
			if (isUnixTimestamp(numericCandidate)) {
				return new Date(numericCandidate * 1000);
			}
		}

		// Fall back to default parsing for date strings
		return this.isDate(date) ? new Date(date) : new Date(+date);
	}

	private isDate(date: DateInput) {
		return new Date(date).toString() !== 'Invalid Date';
	}

	public isSameDay(date1: Date, date2: Date): boolean {
		return date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
	}

	public isCurrentDay(date: Date): boolean {
		return this.isSameDay(date, this.getCurrentDate());
	}

	public dateGridFormatter(value: string | number): string {
		if (!value) {
			return '';
		}

		const baseDate = this.buildBaseDate(value);
		if (isNaN(baseDate.getTime())) {
			return 'INVALID DATE';
		}

		return baseDate.toLocaleString();
	}

	public formatCustom(date: DateInput, options?: Intl.DateTimeFormatOptions, locale: string = 'en-US'): string {
		const baseDate = this.buildBaseDate(date);
		return baseDate.toLocaleString(locale, options);
	}

	/** Returns the start of the week (Monday) for the given date */
	public getWeekStart(date: Date): Date {
		const d = new Date(date);
		const dayOfWeek = d.getDay() === 0 ? 6 : d.getDay() - 1;
		d.setDate(d.getDate() - dayOfWeek);
		d.setHours(0, 0, 0, 0);
		return d;
	}

	/** Returns the end of the week (Sunday) for the given date */
	public getWeekEnd(date: Date): Date {
		const start = this.getWeekStart(date);
		const end = new Date(start);
		end.setDate(start.getDate() + 6);
		end.setHours(23, 59, 59, 999);
		return end;
	}

	/** Normalizes a date to 00:00:00.000 */
	public normalizeDate(date: Date): Date {
		const d = new Date(date);
		d.setHours(0, 0, 0, 0);
		return d;
	}

	/** Converts input to Date if not already a Date */
	public toDate(x: any): Date {
		return x instanceof Date ? x : new Date(x);
	}

	/** Returns ISO week number for a given date */
	public getWeekNumber(date: Date): number {
		const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
		const dayNum = d.getUTCDay() || 7;
		d.setUTCDate(d.getUTCDate() + 4 - dayNum);
		const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
		return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
	}

	public isCurrentWeek(date: Date): boolean {
		const today = this.getCurrentDate();
		const weekStart = this.getWeekStart(today);
		const weekEnd = this.getWeekEnd(today);
		const d = this.normalizeDate(date);
		return d >= weekStart && d <= weekEnd;
	}
}

export function isUnixTimestamp(value: string | number): boolean {
	// Convert to string and check if it's numeric
	const strValue = String(value);

	// If it's in scientific notation or not a number, parse it first
	const numValue = Number(strValue);
	if (isNaN(numValue)) {
		return false;
	}

	const timestamp = Math.floor(numValue);
	const minTimestamp = 0; // 1970-01-01
	const maxTimestamp = 4102444800; // 2100-01-01

	return timestamp >= minTimestamp && timestamp <= maxTimestamp;
}

export function isUnixTimestampWithMilliseconds(value: string | number): boolean {
	// Convert to string and check if it's numeric
	const strValue = String(value);

	// If it's not a number, return false
	const numValue = Number(strValue);
	if (isNaN(numValue)) {
		return false;
	}

	const timestamp = Math.floor(numValue);
	const minTimestamp = 0; // 1970-01-01
	const maxTimestamp = 4102444800000; // 2100-01-01

	// Check if the timestamp is in milliseconds range (13 digits typically)
	// Ensure it's in milliseconds, not seconds
	return timestamp >= minTimestamp && timestamp <= maxTimestamp && timestamp > 1000000000000;
}
